import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { LogOut, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCategory = (category) => {
  if (!category) return "-";
  const map = {
    new: "New",
    article: "Article/Paper",
    course: "Course",
    report: "Report",
    thesis: "Thesis",
    book: "Book",
    note: "Note",
    preprint: "Preprint",
  };
  return map[category.toLowerCase()] || category;
};

const normalizeOrg = (org) => {
  const raw = (org || "").trim();
  if (!raw) return "Public";
  if (raw === "PublicPublic") return "Public";
  if (raw.toLowerCase() === "public") return "Public";
  return raw;
};

const AdminSubmissionSimilarityPage = ({ user, setUser }) => {
  const { id } = useParams(); // submission id
  const location = useLocation();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(location.state?.submission || null);
  const [matchedItem, setMatchedItem] = useState(null);
  const [percent, setPercent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Authentication required.");
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        // If we don't have submission details (e.g. direct URL visit), fetch list and find it
        if (!submission) {
          const listResp = await axios.get(`${API}/admin/submissions`, { headers });
          const found = (listResp.data || []).find((s) => s.id === id);
          if (!found) {
            setError("Submission not found.");
            return;
          }
          setSubmission(found);
        }

        // Compute similarity against existing items
        const simResp = await axios.get(`${API}/admin/submissions/${id}/similarity`, { headers });
        const { similarity, matched_item_id } = simResp.data || {};
        const pct = Math.round((similarity || 0) * 100);
        setPercent(pct);

        if (!matched_item_id) {
          setMatchedItem(null);
          return;
        }

        const itemResp = await axios.get(`${API}/items/${matched_item_id}`, { headers });
        setMatchedItem(itemResp.data);
      } catch (err) {
        console.error("Submission similarity error:", err);
        setError(
          err.response?.data?.detail ||
            "Error loading similarity comparison for this submission.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-600 text-sm">
        Admin access required.
      </div>
    );
  }

  const renderSubmissionRow = () => {
    if (!submission) return null;
    return (
      <tr>
        <td className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Submission
        </td>
        <td className="px-4 py-3">
          <div className="space-y-1">
            <div className="font-medium text-slate-900" data-testid="submission-title">
              {submission.title}
            </div>
            <div className="text-xs text-slate-600">
              {submission.author_name || submission.submitted_by_email || "No author"}
            </div>
            <div className="text-xs text-slate-500">
              Category: {formatCategory(submission.category)} · Submission type: {submission.submission_type || "-"} · Words: {submission.word_count?.toLocaleString() || "-"}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-right align-middle text-xs text-slate-500">—</td>
      </tr>
    );
  };

  const renderMatchedRow = () => {
    if (!matchedItem) return null;
    return (
      <tr>
        <td className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          Similar item
        </td>
        <td className="px-4 py-3">
          <div className="space-y-1">
            <div className="font-medium text-slate-900" data-testid="similar-title">
              {matchedItem.title}
            </div>
            <div className="text-xs text-slate-600">
              {matchedItem.authors && matchedItem.authors.length > 0
                ? matchedItem.authors.join(", ")
                : "No authors"}
            </div>
            <div className="text-xs text-slate-500">
              Year: {matchedItem.year || "-"} · Category: {formatCategory(matchedItem.category)} · Org: {normalizeOrg(matchedItem.organization)}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-right align-middle">
          {percent != null ? (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-900 text-white">
              {percent}%
            </span>
          ) : (
            <span className="text-xs text-slate-500">-</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1
              className="text-xl font-semibold"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Admin Dashboard
            </h1>
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="submissions" />

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2
              className="text-2xl font-bold mb-1"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Submission similarity comparison
            </h2>
            <p className="text-xs text-slate-500 max-w-xl">
              Submission on the left, most similar item on the right, with similarity percentage.
              High similarity may indicate duplication or plagiarism.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/admin/submissions")}
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to submissions
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center text-sm text-slate-600">
            Loading similarity details...
            <BlueCubeSpinner />
          </div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : !submission ? (
          <div className="text-sm text-slate-600">Submission not found.</div>
        ) : !matchedItem ? (
          <div className="text-sm text-slate-600">
            No significantly similar item found
            {percent != null ? ` (similarity ${percent}%)` : ""}.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider w-40">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider w-32">
                    Similarity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {renderSubmissionRow()}
                {renderMatchedRow()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissionSimilarityPage;
